import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { readEntityClusterSummary, readSingleEntitySummary } from '@/components/map/utils/entityClusterProperties';
import type { EntityClusterSummary } from '@/components/map/utils/entityClusterProperties';
import { useClusterCardHover } from '@/hooks/useClusterCardHover';
import type { ClusterCardHoverOptions } from '@/hooks/useClusterCardHover';

export interface PoiClusterInteractionState {
    /**
     * The split-model breakdown read from the cluster's entity accumulators. A cluster
     * without them (a source this build never creates) is ignored rather than shown as
     * an empty card.
     */
    hoveredEntitySummary: EntityClusterSummary | null;
    /** Geographic [lng, lat] of the hovered cluster — the MapLibre `<Popup>` anchor. */
    hoveredClusterLngLat: [number, number] | null;
    onClusterCardEnter: () => void;
    onClusterCardLeave: () => void;
}

const SINGLE_CLIENT_CLUSTER_OPTIONS: ClusterCardHoverOptions = {
    // Every network-level bubble carries the breakdown card: the clusters and the "1"
    // bubbles a lone record is drawn as, both below POI_MARKER_MIN_ZOOM. From the marker
    // handover inward the viewport source is unclustered, so there is no bubble to hover.
    layerIds: [ORBIT_LAYER_IDS.poiNetworkClusters, ORBIT_LAYER_IDS.poiNetworkUnclustered],
    // A cluster is keyed by its cluster id and must carry the family accumulators the card
    // reads; a lone record is keyed by its own entity id. Anything else (unreachable with
    // the entity source) is ignored. The two id spaces never collide: one is numeric, the
    // other a record id.
    keyFor: (feature) => {
        const clusterId = feature.properties['cluster_id'] as number | undefined;
        if (clusterId != null) {
            return typeof feature.properties['count_pois'] === 'number' ? `cluster:${String(clusterId)}` : null;
        }
        const entityId: unknown = feature.properties['id'];
        return typeof entityId === 'string' && entityId.length > 0 ? `single:${entityId}` : null;
    },
    readSummary: (props) =>
        props['cluster_id'] != null ? readEntityClusterSummary(props) : readSingleEntitySummary(props),
    // No click of its own: the bubble click ease-zooms in useEntitiesLayer, which owns the
    // source. The shared hook still drops the card the moment the click lands.
};

/**
 * Hover state for the single-client POI network bubbles, clusters and "1" bubbles alike. The
 * hover machinery is shared with the Network Explorer's bubbles (useClusterCardHover); this
 * hook supplies the bubble layers, the keys and the summary reader. A cluster's per-status
 * breakdown is read from its accumulated count_* properties, a lone record's from its own
 * entity type and status (see entityClusterProperties), so no async leaf query is needed.
 */
export function usePoiClusterInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    enabled: boolean = true,
): PoiClusterInteractionState {
    const { hoveredSummary, hoveredLngLat, onCardEnter, onCardLeave } = useClusterCardHover(
        mapRef,
        isMapLoaded,
        enabled,
        SINGLE_CLIENT_CLUSTER_OPTIONS,
    );
    return {
        hoveredEntitySummary: hoveredSummary,
        hoveredClusterLngLat: hoveredLngLat,
        onClusterCardEnter: onCardEnter,
        onClusterCardLeave: onCardLeave,
    };
}
